import { Box, Button, Stack } from "@justworkshr/milo-core";
import {
  CUSTOMER_IO_CTA_CLICK_EVENT,
  sendCustomerIOEvent,
} from "app/customer-io";
import { oktaAuth, pkceIsEnabled } from "app/okta";
import { FLAG_NAMES, flagIsEnabled } from "lib/environment";
import { ReactElement, useState } from "react";
import {
  useGetCompanyDisplayQuery,
  useGetIsEmployerOfRecordEnabledQuery,
} from "types/generated/operations";
import ThankYouDialog from "../../../international-solutions/components/Dialog/ThankYouDialog";
import { CF_ADD_EMPLOYEES_LINK, EOR_PRICE } from "../../constants";
import styles from "../../InternationalSolutionsPage.module.css";

const EORPriceCard = (): ReactElement => {
  const { mdHead, hr, smFont, secondaryFont, xlFont } = styles;

  const [showThankYouDialog, setShowThankYouDialog] = useState(false);
  const handleThankYouDialog = () => {
    setShowThankYouDialog((prev) => !prev);
  };

  const { data: eorEnabledData, loading } =
    useGetIsEmployerOfRecordEnabledQuery();
  const isEmployerOfRecordEnabled =
    eorEnabledData?.authenticatedMember.company.companySetting?.value ===
      "true" && flagIsEnabled(FLAG_NAMES.EMPLOYER_OF_RECORD);

  const accessToken = pkceIsEnabled() ? oktaAuth.getAccessToken() : "";
  const { data: memberData } = useGetCompanyDisplayQuery();

  const triggerCustomerIOEvent = (ctaName: string, nextUrl: string) => {
    sendCustomerIOEvent(
      {
        event_type: CUSTOMER_IO_CTA_CLICK_EVENT,
        member_uuids: [memberData?.authenticatedMember.uuid ?? ""],
        company_uuids: [memberData?.authenticatedMember.company.uuid ?? ""],
        data: {
          current_url: window.location.href,
          cta_name: ctaName,
          next_url: nextUrl,
        },
      },
      accessToken
    );
  };

  return (
    <Box
      backgroundColor="neutral"
      border={{
        borderColor: "neutralSubtle",
        borderRadius: "md",
        borderWidth: "sm",
      }}
      padding="md"
    >
      <Stack spacing="md">
        <Stack spacing="2xs">
          <p className={mdHead}>Pricing</p>
          <p className={smFont}>No setup fees, or minimums</p>
        </Stack>
        <hr className={hr}></hr>
        <Stack spacing="lg">
          <Stack spacing="2xs">
            <p className={xlFont}>{EOR_PRICE}</p>
            <p className={`${smFont} ${secondaryFont}`}>
              For full-time, international employees.
            </p>
          </Stack>
          {isEmployerOfRecordEnabled ? (
            <Button
              data-heap-id="eor-add-employees-button"
              color="brand"
              type="button"
              size="md"
              as="a"
              href={CF_ADD_EMPLOYEES_LINK}
              onClick={() => {
                triggerCustomerIOEvent(
                  "eor_eor_add_employee_button",
                  CF_ADD_EMPLOYEES_LINK
                );
              }}
            >
              Get started
            </Button>
          ) : (
            <Button
              data-heap-id="no-eor-im-interested-button"
              color="brand"
              type="button"
              size="md"
              onClick={() => {
                triggerCustomerIOEvent(
                  "eor_eor_get_started_button",
                  "thank-you-modal"
                );
                handleThankYouDialog();
              }}
              loading={loading}
            >
              Get started
            </Button>
          )}
        </Stack>
      </Stack>

      {showThankYouDialog && (
        <ThankYouDialog
          isOpen={showThankYouDialog}
          onClose={handleThankYouDialog}
        />
      )}
    </Box>
  );
};

export default EORPriceCard;
